import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import type { DrinkEntry, Drink, UserData } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useDrinkLog } from '@/hooks/useDrinkLog'
import { useTicker } from '@/hooks/useTicker'
import NavBar from '@/components/layout/NavBar'
import SideMenu from '@/components/layout/SideMenu'
import DevBanner from '@/components/layout/DevBanner'
import SyncStatusBanner from '@/components/layout/SyncStatusBanner'
import PasswordLock from '@/components/layout/PasswordLock'
import BACCard from '@/components/dashboard/BACCard'
import SoberCountdown from '@/components/dashboard/SoberCountdown'
import AwarenessBar from '@/components/dashboard/AwarenessBar'
import InsightPanels from '@/components/dashboard/InsightPanels'
import StatsPanel from '@/components/dashboard/StatsPanel'
import DrinkForm from '@/components/drinks/DrinkForm'
import GapForm from '@/components/drinks/GapForm'
import DrinkHistory from '@/components/drinks/DrinkHistory'
import TrendsChart from '@/components/charts/TrendsChart'
import SettingsModal from '@/components/settings/SettingsModal'
import OnboardingWizard from '@/components/onboarding/OnboardingWizard'
import CelebrationModal from '@/components/dashboard/CelebrationModal'
import Awards from '@/components/dashboard/Awards'
import Analytics from '@/components/analytics/Analytics'
import Help from '@/pages/Help'
import Feedback from '@/pages/Feedback'
import Features from '@/pages/Features'
import About from '@/pages/About'
import Security from '@/pages/Security'
import KeyRecovery from '@/pages/KeyRecovery'
import { generateReport } from '@/utils/report'
import PasscodeReminder from '@/components/dashboard/PasscodeReminder'
import { deleteUserData } from '@/utils/api'
import { computeEarnedMilestones, getLiveStreakSeconds, TIER_LABELS, type EarnedMilestone } from '@/utils/milestones'
import { sendMilestoneNotification } from '@/utils/notifications'

// Default drink database - will be loaded from public/drinks.json
const defaultDrinks: Drink[] = []

export default function Dashboard() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { data, isLoading, isInitialized, error, needsDecryption, loadMode, offlineSince, saveError, retrySave, dismissSaveError, updateData, addEntry, updateEntry, deleteEntry, supplyPassword, clearPassword, setPasswordForEncryption, restoreEncryptionKey, removeEncryption, getCachedPassword, cachePassword } = useUser(userId)
  const { tick } = useTicker()

  // Password lock state
  const [isLocked, setIsLocked] = useState(false)
  const [isTimeoutLock, setIsTimeoutLock] = useState(false)
  const lastActivityRef = useRef(Date.now())

  // Consume password from navigation state for E2E decryption
  useEffect(() => {
    if (!isInitialized || !needsDecryption) return
    const navPassword = (location.state as { password?: string })?.password
    if (navPassword) {
      supplyPassword(navPassword).then(success => {
        if (success) {
          // Clear the password from navigation state
          navigate(location.pathname, { replace: true, state: {} })
        }
      })
    }
  }, [isInitialized, needsDecryption, location.state]) // eslint-disable-line react-hooks/exhaustive-deps

  // Check if password lock is needed on load
  useEffect(() => {
    if (!isInitialized || !data.passwordHash) return

    // For passwordMode === 'on_load', the user wants to re-enter the
    // password on every visit. Always show the lock screen — sessionStorage
    // would suppress it on reload within the same tab, leaving the
    // password cache empty and breaking auth-gated endpoints later.
    if (data.passwordMode === 'on_load') {
      sessionStorage.removeItem(`ssdl:unlocked:${userId}`)
      setIsLocked(true)
      setIsTimeoutLock(false)
      return
    }

    // For after_timeout (or undefined), sessionStorage tracks unlock
    // state across reloads within the tab.
    const sessionValid = sessionStorage.getItem(`ssdl:unlocked:${userId}`)
    if (!sessionValid) {
      setIsLocked(true)
      setIsTimeoutLock(false)
    }
  }, [isInitialized, data.passwordHash, data.passwordMode, userId])

  // Activity tracking for timeout lock
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const resetTimeoutLock = useCallback(() => {
    lastActivityRef.current = Date.now()
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (data.passwordHash && data.passwordMode === 'after_timeout' && data.timeoutMinutes && !isLocked) {
      timeoutRef.current = setTimeout(() => {
        setIsLocked(true)
        setIsTimeoutLock(true)
        sessionStorage.removeItem(`ssdl:unlocked:${userId}`)
      }, data.timeoutMinutes * 60 * 1000)
    }
  }, [data.passwordHash, data.passwordMode, data.timeoutMinutes, isLocked, userId])

  useEffect(() => {
    if (!data.passwordHash || data.passwordMode !== 'after_timeout' || !data.timeoutMinutes) return

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(event => window.addEventListener(event, resetTimeoutLock, { passive: true }))

    // Start the initial timeout
    resetTimeoutLock()

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimeoutLock))
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [data.passwordHash, data.passwordMode, data.timeoutMinutes, isLocked, resetTimeoutLock])

  // Handle password unlock via server verification
  const handleUnlock = useCallback(async (password: string): Promise<boolean> => {
    if (!data.passwordHash) return false

    // If data needs decryption, the password is verified by successful decryption
    if (needsDecryption) {
      const decrypted = await supplyPassword(password)
      if (!decrypted) return false
    } else {
      // Verify password server-side
      try {
        const response = await fetch(`/api/user/${encodeURIComponent(userId!)}/password/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        })
        const result = await response.json()
        if (!result.valid) return false
      } catch {
        return false
      }

      // Re-initialize encryption key for saving (e.g. after timeout lock)
      if (data.encrypted && data.encryptionSalt) {
        await restoreEncryptionKey(password)
      } else {
        // Account has a password but no E2E encryption (e.g. user set
        // a password on a previously-unencrypted account). Cache the
        // raw password so subsequent auth-gated endpoints like
        // PATCH /settings can include it as currentPassword without
        // re-prompting the user.
        cachePassword(password)
      }
    }

    setIsLocked(false)
    setIsTimeoutLock(false)
    lastActivityRef.current = Date.now()
    sessionStorage.setItem(`ssdl:unlocked:${userId}`, 'true')
    return true
  }, [data.passwordHash, data.encrypted, data.encryptionSalt, needsDecryption, userId, supplyPassword, restoreEncryptionKey, cachePassword])

  // Manual lock function for side menu
  const handleManualLock = useCallback(() => {
    if (data.passwordHash) {
      sessionStorage.removeItem(`ssdl:unlocked:${userId}`)
      clearPassword()
      setIsLocked(true)
      setIsTimeoutLock(false)
    }
  }, [data.passwordHash, userId, clearPassword])

  // Logout function - clears localStorage and redirects to home
  const handleLogout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem('ssdl_data')
    // Clear session storage
    sessionStorage.removeItem(`ssdl:unlocked:${userId}`)
    // Clear encryption key
    clearPassword()
    // Navigate to home
    navigate('/')
  }, [userId, navigate, clearPassword])

  // Auto-logout timer
  const autoLogoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveErrorRef = useRef<typeof saveError>(null)
  useEffect(() => {
    saveErrorRef.current = saveError
  }, [saveError])

  const resetAutoLogout = useCallback(() => {
    if (autoLogoutTimerRef.current) {
      clearTimeout(autoLogoutTimerRef.current)
    }
    if (data.autoLogoutMinutes && data.autoLogoutMinutes > 0) {
      autoLogoutTimerRef.current = setTimeout(() => {
        // Refuse to wipe localStorage if a save is still unresolved — it may
        // be the only copy of drinks the server never accepted.
        if (saveErrorRef.current) {
          console.warn('Auto-logout blocked: unresolved save error would destroy unsynced drinks')
          return
        }
        handleLogout()
      }, data.autoLogoutMinutes * 60 * 1000)
    }
  }, [data.autoLogoutMinutes, handleLogout])

  useEffect(() => {
    if (!data.autoLogoutMinutes || data.autoLogoutMinutes <= 0) {
      if (autoLogoutTimerRef.current) {
        clearTimeout(autoLogoutTimerRef.current)
        autoLogoutTimerRef.current = null
      }
      return
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(event => window.addEventListener(event, resetAutoLogout, { passive: true }))

    // Start the initial timer
    resetAutoLogout()

    return () => {
      events.forEach(event => window.removeEventListener(event, resetAutoLogout))
      if (autoLogoutTimerRef.current) {
        clearTimeout(autoLogoutTimerRef.current)
      }
    }
  }, [data.autoLogoutMinutes, resetAutoLogout])

  // Load drinks database
  const [databaseDrinks, setDatabaseDrinks] = useState<Drink[]>(defaultDrinks)
  useEffect(() => {
    fetch('/drinks.json')
      .then(res => res.json())
      .then(data => setDatabaseDrinks(data.drinks || []))
      .catch(console.error)
  }, [])

  // Computed drink data
  const drinkLog = useDrinkLog(data.entries, data.drinks, databaseDrinks, tick)

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [onboardingJustCompleted, setOnboardingJustCompleted] = useState(false)

  useEffect(() => {
    if (isInitialized && data.entries.length === 0 && !data.passwordHash && !isLocked) {
      const onboarded = localStorage.getItem(`ssdl:onboarded:${userId}`)
      if (!onboarded) {
        setShowOnboarding(true)
      }
    }
  }, [isInitialized]) // eslint-disable-line react-hooks/exhaustive-deps

  // Open drink form after onboarding wizard completes
  useEffect(() => {
    if (onboardingJustCompleted && !showOnboarding) {
      setEditingEntry(null)
      setEditingIndex(null)
      setDrinkFormOpen(true)
      setOnboardingJustCompleted(false)
    }
  }, [onboardingJustCompleted, showOnboarding])

  // UI state
  const [menuOpen, setMenuOpen] = useState(false)
  const [drinkFormOpen, setDrinkFormOpen] = useState(false)
  const [gapFormOpen, setGapFormOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsSection, setSettingsSection] = useState<string | undefined>()
  // URL-based sub-page detection
  const subPage = location.pathname.replace(`/u/${userId}`, '').replace(/^\//, '')

  const [celebrationMilestone, setCelebrationMilestone] = useState<EarnedMilestone | null>(null)
  const celebratedRef = useRef<Set<string>>(new Set())
  const [editingEntry, setEditingEntry] = useState<DrinkEntry | null>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [chartPeriod, setChartPeriod] = useState<'week' | 'month' | 'year' | 'all'>('month')

  // Milestone celebration detection
  const liveStreakSeconds = getLiveStreakSeconds(drinkLog.soberStreaks, drinkLog.currentBAC)
  const allStreaksForMilestones = drinkLog.soberStreaks

  // Default to the Awards page on initial load when the user is sober.
  // We make this decision exactly once — as soon as the app is ready — then
  // never again, so users who manually navigate back to the dashboard while
  // still sober aren't bounced back to /awards.
  const sobrietyRedirectRef = useRef(false)
  useEffect(() => {
    if (sobrietyRedirectRef.current) return
    if (!isInitialized || isLoading) return
    if (isLocked || needsDecryption) return
    if (showOnboarding) return
    sobrietyRedirectRef.current = true
    if (subPage !== '') return
    if (data.entries.length === 0) return
    if (drinkLog.currentBAC > 0) return
    navigate(`/u/${userId}/awards`, { replace: true })
  }, [isInitialized, isLoading, isLocked, needsDecryption, showOnboarding, subPage, data.entries.length, drinkLog.currentBAC, userId, navigate])

  useEffect(() => {
    if (allStreaksForMilestones.length === 0) return

    // Load previously celebrated milestones from localStorage
    const storageKey = `ssdl:celebrated:${userId}`
    if (celebratedRef.current.size === 0) {
      try {
        const stored = localStorage.getItem(storageKey)
        if (stored) {
          const ids = JSON.parse(stored) as string[]
          ids.forEach(id => celebratedRef.current.add(id))
        }
      } catch { /* ignore */ }
    }

    const earned = computeEarnedMilestones(allStreaksForMilestones)
    // Only celebrate milestones earned by the currently live streak
    const liveStreak = allStreaksForMilestones.find(s => s.isLive)
    const liveEarned = liveStreak
      ? earned.filter(m => m.streakStartedAt === liveStreak.startedAt)
      : []
    // Find first uncelebrated milestone — track by milestone+streak so re-earning bursts again
    const uncelebrated = liveEarned.find(m => !celebratedRef.current.has(`${m.id}:${m.streakStartedAt}`))
    if (uncelebrated) {
      setCelebrationMilestone(uncelebrated)
      // Send browser notification (works even if tab is in background)
      const tierLabel = `${TIER_LABELS[uncelebrated.tier]} ${uncelebrated.tierLevel}`
      sendMilestoneNotification(uncelebrated.icon, uncelebrated.name, tierLabel, uncelebrated.healthFact)
      celebratedRef.current.add(`${uncelebrated.id}:${uncelebrated.streakStartedAt}`)
      try {
        localStorage.setItem(storageKey, JSON.stringify([...celebratedRef.current]))
      } catch { /* ignore */ }
    }
  }, [allStreaksForMilestones, userId])

  // Get last drink for form pre-fill
  const lastDrink = data.entries.filter(e => !e.is_gap).slice(-1)[0]?.drink || null
  const lastDrinkTime = data.entries.filter(e => !e.is_gap).slice(-1)[0]?.finished_at || null

  // Handle save drink
  const handleSaveDrink = useCallback((entry: DrinkEntry) => {
    if (editingIndex !== null) {
      updateEntry(editingIndex, entry)
    } else {
      addEntry(entry)
    }
    setEditingEntry(null)
    setEditingIndex(null)
  }, [editingIndex, updateEntry, addEntry])

  // Handle save gap
  const handleSaveGap = useCallback((entry: DrinkEntry) => {
    addEntry(entry)
  }, [addEntry])

  // Handle edit entry
  const handleEdit = useCallback((index: number) => {
    setEditingEntry(data.entries[index])
    setEditingIndex(index)
    setDrinkFormOpen(true)
  }, [data.entries])

  // Handle copy entry (open form pre-filled with drink data, keep original time)
  const handleCopy = useCallback((index: number) => {
    const entry = data.entries[index]
    setEditingEntry({ ...entry })
    setEditingIndex(null) // null means new entry
    setDrinkFormOpen(true)
  }, [data.entries])

  // Handle delete entry
  const handleDelete = useCallback((index: number) => {
    deleteEntry(index)
  }, [deleteEntry])

  // Handle settings save
  const handleSettingsSave = useCallback(async (settings: {
    weight: number
    gender: 'male' | 'female'
    timeoutMinutes?: number
    autoLogoutMinutes: number
    passwordHash?: string
    passwordSalt?: string
    passwordIterations?: number
    passwordMode?: 'on_load' | 'after_timeout'
    backupEmail?: string
    rawPassword?: string
  }) => {
    if (settings.rawPassword) {
      // Password set or changed — initialize encryption
      await setPasswordForEncryption(settings.rawPassword)
      updateData({ ...settings, encrypted: true })
    } else if (!settings.passwordHash && data.encrypted) {
      // Password removed — remove encryption
      removeEncryption()
      updateData({ ...settings, encrypted: false, encryptionSalt: undefined, encryptedData: undefined })
    } else {
      updateData(settings)
    }
  }, [updateData, setPasswordForEncryption, removeEncryption, data.encrypted])

  // Download GP report
  const handleDownloadReport = useCallback(() => {
    generateReport({
      entries: data.entries,
      weight: data.weight,
      gender: data.gender,
      allSessions: drinkLog.allSessions,
    })
  }, [data.entries, data.weight, data.gender, drinkLog.allSessions])

  // Download all data as JSON
  const handleDownloadData = useCallback(() => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ssdl-data-${userId}-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [data, userId])

  // Import data from JSON file
  const handleImportData = useCallback((imported: Partial<UserData>) => {
    updateData({
      weight: imported.weight ?? data.weight,
      gender: imported.gender ?? data.gender,
      entries: imported.entries ?? [],
      drinks: imported.drinks ?? [],
    })
  }, [updateData, data.weight, data.gender])

  // Delete all data
  const handleDeleteData = useCallback(async () => {
    try {
      if (!import.meta.env.DEV) {
        await deleteUserData(userId!)
      }
      localStorage.removeItem('ssdl_data')
      sessionStorage.removeItem(`ssdl:unlocked:${userId}`)
      navigate('/')
    } catch (e) {
      console.error('Failed to delete data:', e)
    }
  }, [userId, navigate])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">Loading your drink log...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="card max-w-md text-center">
          <svg
            className="w-12 h-12 text-[var(--bac-danger)] mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
            Failed to Load
          </h2>
          <p className="text-[var(--text-secondary)] mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const isDev = import.meta.env.DEV

  // If decrypting via navigation state password, show loading instead of lock
  const hasNavPassword = !!(location.state as { password?: string })?.password
  if (needsDecryption && hasNavPassword) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">Decrypting your data...</p>
        </div>
      </div>
    )
  }

  // Show password lock if locked or data needs decryption
  if ((isLocked || needsDecryption) && data.passwordHash) {
    return (
      <PasswordLock
        onUnlock={handleUnlock}
        isTimeout={isTimeoutLock}
      />
    )
  }

  // Show onboarding wizard for new users
  if (showOnboarding && !isLocked) {
    return (
      <OnboardingWizard
        userId={userId!}
        weight={data.weight}
        gender={data.gender}
        onComplete={async (settings) => {
          // If password was set, initialize E2E encryption
          if (settings.rawPassword) {
            await setPasswordForEncryption(settings.rawPassword)
            updateData({ ...settings, encrypted: true })
          } else {
            updateData(settings)
          }
          if (settings.passwordHash) {
            sessionStorage.setItem(`ssdl:unlocked:${userId}`, 'true')
          }
          localStorage.setItem(`ssdl:onboarded:${userId}`, 'true')
          setOnboardingJustCompleted(true)
          setShowOnboarding(false)
        }}
      />
    )
  }

  return (
    <div className="flex flex-col min-h-screen">
      <DevBanner />
      <div className={isDev ? 'pt-7' : ''}>
      <NavBar
        onMenuClick={() => setMenuOpen(true)}
        onLogDrink={() => {
          setEditingEntry(null)
          setEditingIndex(null)
          setDrinkFormOpen(true)
        }}
        onLogGap={() => setGapFormOpen(true)}
        onDownloadReport={handleDownloadReport}
        onAwardsClick={() => navigate(`/u/${userId}/awards`)}
        onAnalyticsClick={() => navigate(`/u/${userId}/analytics`)}
        onOpenSettings={(section?: string) => { setSettingsSection(section); setSettingsOpen(true) }}
        riskAssessment={drinkLog.riskAssessment}
        soberStreakSeconds={liveStreakSeconds}
      />

      <SideMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onLogDrink={() => {
          setEditingEntry(null)
          setEditingIndex(null)
          setDrinkFormOpen(true)
        }}
        onLogGap={() => setGapFormOpen(true)}
        onLockScreen={data.passwordHash ? handleManualLock : undefined}
        onLogout={handleLogout}
      />

      {subPage === 'awards' ? (
        <Awards
          soberStreaks={drinkLog.soberStreaks}
          currentBAC={drinkLog.currentBAC}
          onClose={() => navigate(`/u/${userId}`)}
        />
      ) : subPage === 'analytics' ? (
        <Analytics
          entries={data.entries}
          onClose={() => navigate(`/u/${userId}`)}
        />
      ) : subPage === 'help' ? (
        <Help embedded />
      ) : subPage === 'feedback' ? (
        <Feedback embedded />
      ) : subPage === 'features' ? (
        <Features embedded />
      ) : subPage === 'about' ? (
        <About embedded />
      ) : subPage === 'security' ? (
        <Security embedded />
      ) : subPage === 'recover' ? (
        <KeyRecovery embedded />
      ) : (
      <main className="flex-1 p-4 max-w-4xl lg:max-w-6xl mx-auto w-full space-y-4">
        {/* Sync status — save failures or offline cache mode */}
        <SyncStatusBanner
          saveError={saveError}
          offlineSince={loadMode === 'offline-cache' ? offlineSince : null}
          onRetry={retrySave}
          onDismiss={dismissSaveError}
        />

        {/* Passcode Reminder */}
        <PasscodeReminder userId={userId!} hasBackupEmail={!!data.backupEmail} />

        {/* Awareness Bar */}
        <AwarenessBar data={drinkLog.awarenessData} />

        {/* BAC Card (40%) + Since Last Drink (30%) + Until Sober (30%) */}
        <div className="flex items-stretch gap-4 overflow-x-auto snap-x snap-mandatory md:grid md:overflow-visible" style={{ gridTemplateColumns: '4fr 3fr 3fr' }}>
          <BACCard
            currentBAC={drinkLog.currentBAC}
            currentSessionDrinks={drinkLog.currentSessionDrinks}
          />
          <SoberCountdown
            secondsSinceLastDrink={drinkLog.secondsSinceLastDrink}
            hoursUntilSober={drinkLog.hoursUntilSober}
            isSober={drinkLog.currentBAC <= 0}
          />
        </div>

        {/* Insight Panels */}
        <InsightPanels
          historicalSessions={
            drinkLog.currentBAC > 0
              ? [...drinkLog.allSessions].slice(0, -1).reverse().slice(0, 5)
              : [...drinkLog.allSessions].reverse().slice(0, 5)
          }
          allSessions={drinkLog.allSessions}
          soberStreaks={drinkLog.soberStreaks}
          tick={tick}
        />

        {/* Stats Panel */}
        <StatsPanel
          currentSessionStats={drinkLog.currentSessionStats}
          lastSessionStats={drinkLog.lastSessionStats}
          totalStats={drinkLog.totalStats}
          totalSessions={drinkLog.sessionCount}
          currentBAC={drinkLog.currentBAC}
          allSessions={drinkLog.allSessions}
        />

        {/* Trends Chart */}
        <TrendsChart
          allSessions={drinkLog.allSessions}
          period={chartPeriod}
          onPeriodChange={setChartPeriod}
        />

        {/* Drink History */}
        <DrinkHistory
          entries={data.entries}
          onEdit={handleEdit}
          onCopy={handleCopy}
          onDelete={handleDelete}
        />
      </main>
      )}

      {/* Modals */}
      <DrinkForm
        isOpen={drinkFormOpen}
        onClose={() => {
          setDrinkFormOpen(false)
          setEditingEntry(null)
          setEditingIndex(null)
        }}
        onSave={handleSaveDrink}
        editingEntry={editingEntry}
        editingIndex={editingIndex}
        myDrinks={drinkLog.sortedDrinks.myDrinks}
        allDrinks={drinkLog.sortedDrinks.allDrinks}
        lastDrink={lastDrink}
        lastDrinkTime={editingIndex != null
          ? data.entries.slice(0, editingIndex).filter(e => !e.is_gap).slice(-1)[0]?.finished_at || null
          : editingEntry ? editingEntry.finished_at
          : lastDrinkTime}
      />

      <GapForm
        isOpen={gapFormOpen}
        onClose={() => setGapFormOpen(false)}
        onSave={handleSaveGap}
        lastDrinkTime={lastDrinkTime}
      />

      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => { setSettingsOpen(false); setSettingsSection(undefined) }}
        section={settingsSection}
        weight={data.weight}
        gender={data.gender}
        timeoutMinutes={data.timeoutMinutes}
        autoLogoutMinutes={data.autoLogoutMinutes ?? 0}
        passwordHash={data.passwordHash}
        passwordSalt={data.passwordSalt}
        passwordMode={data.passwordMode}
        backupEmail={data.backupEmail}
        onSave={handleSettingsSave}
        userId={userId || ''}
        getCachedPassword={getCachedPassword}
        onDownloadData={handleDownloadData}
        onImportData={handleImportData}
        onDeleteData={handleDeleteData}
      />

      {/* Celebration modal */}
      <CelebrationModal
        milestone={celebrationMilestone}
        onClose={() => setCelebrationMilestone(null)}
      />

      </div>
    </div>
  )
}
